/**
 * Loads the official Memberfy SDK from the live API server.
 * The server generates sdk.js from its own routes, so the file is never stale.
 * DEV  -> https://api.memberfy.dev/sdk.js
 * PROD -> https://api.memberfy.net/sdk.js
 */

export interface MemberfyErrorShape {
  name: string
  message: string
  status: number
  body: any
  errors?: any
}

export interface OfficialClient {
  setToken: (token: string) => OfficialClient
  setCommunity: (communityId: string) => OfficialClient
  setLanguage: (language: string) => OfficialClient
  getToken: () => string
  config: Record<string, any>
  request: (spec: {
    httpMethod: string
    path: string
    pathParams?: string[]
    queryParams?: string[]
    hasBody?: boolean
  }, args?: Record<string, any>) => Promise<any>
  [group: string]: any
}

/**
 * Where the API lives, which on a community's own domain is **somewhere else**.
 *
 * Exported because the boot screen has to be able to name it: when this origin
 * is unreachable the person sees a failure on their own domain, and the only
 * useful thing to tell them is which other address did not answer.
 */
export const getApiOrigin = (): string => {
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl) {
    return envUrl.replace(/\/api\/?$/, '')
  }

  const hostname = window.location.hostname
  if (hostname === 'app.memberfy.net') {
    return 'https://api.memberfy.net'
  }

  return 'https://api.memberfy.dev'
}

export const getSdkUrl = (): string => getApiOrigin() + '/sdk.js'

let officialClient: OfficialClient | null = null
let loadingPromise: Promise<OfficialClient> | null = null

export const loadOfficialSdk = (): Promise<OfficialClient> => {
  if (officialClient) return Promise.resolve(officialClient)
  if (loadingPromise) return loadingPromise

  loadingPromise = new Promise((resolve, reject) => {
    const w = window as any
    const existing = w.Memberfy
    if (existing?.createClient) {
      officialClient = existing.createClient({ baseUrl: getApiOrigin() }) as OfficialClient
      resolve(officialClient)
      return
    }

    const script = document.createElement('script')
    script.src = getSdkUrl()
    script.async = true
    script.onload = () => {
      const Memberfy = (window as any).Memberfy
      if (!Memberfy?.createClient) {
        loadingPromise = null
        /* English and technical on purpose: this string is not UI copy, it is
           the `detail` line of a diagnostics block somebody pastes to support.
           A sentence in one language inside a screen in another helps nobody. */
        reject(new Error('SDK script loaded but did not expose createClient — the response was probably not our script.'))
        return
      }
      officialClient = Memberfy.createClient({ baseUrl: getApiOrigin() }) as OfficialClient
      resolve(officialClient)
    }
    script.onerror = () => {
      loadingPromise = null
      /*
       * Tagged, because this failure has a specific cause worth telling apart:
       * the browser could not fetch a script from another origin. On a
       * corporate network that is almost always a proxy, and reporting it as
       * "community not found" sends the person to look at their own domain.
       *
       * `onerror` on a script tag carries no status and no message — the
       * browser withholds cross-origin detail — so the code is all we get.
       */
      const error = new Error('Script request failed — blocked, offline, DNS or TLS.') as Error & { code?: string; url?: string }
      error.code = 'SDK_UNREACHABLE'
      error.url = getSdkUrl()
      reject(error)
    }
    document.head.appendChild(script)
  })

  return loadingPromise
}

export const getOfficialClient = (): OfficialClient | null => officialClient
